import React from 'react';
import { Button } from "@/components/ui/button";
import { Link } from 'react-router-dom';
import { ArrowRight, Shield, UserPlus } from 'lucide-react';
import { useSetupProgress } from "@/hooks/useSetupProgress";
import { getCurrentUserName } from "@/lib/auth";
import { useCompactMode } from "@/hooks/useCompactMode";

const GettingStarted = () => {
  const { setupSteps } = useSetupProgress();
  const firstName = getCurrentUserName();
  const completedSteps = setupSteps.filter(step => step.completed).length;
  const { cx } = useCompactMode();

  return <div className={cx("px-6 py-6 space-y-6 max-w-5xl mx-auto", "px-4 py-4 space-y-4 max-w-5xl mx-auto")}>
      <div className={cx("mb-6 space-y-2 text-center", "mb-4 space-y-1 text-center")}>
        <h1 className={cx("text-3xl tracking-tight font-semibold", "text-2xl tracking-tight font-semibold")}>
          {firstName ? `Hola ${firstName}! Welcome to Smplify 👋` : 'Welcome to Smplify! 👋'}
        </h1>
        <p className={cx("text-muted-foreground mx-auto max-w-3xl leading-relaxed", "text-sm text-muted-foreground mx-auto max-w-3xl leading-relaxed")}>
          Here is your all-in-one solution for device management, configuration deployment, and security enforcement. 
          Let's get your setup tailored to your needs.
        </p>
      </div>

      <div className={cx("bg-card rounded-lg p-6 mb-10 shadow-sm border flex flex-col md:flex-row items-center py-[24px] my-[40px]", "bg-card rounded-lg p-4 mb-6 shadow-sm border flex flex-col md:flex-row items-center py-4 my-6")}>
        <div className="md:w-3/5 mb-3 md:mb-0 pl-2">
          <h2 className={cx("tracking-tight mb-2 text-xl font-medium", "tracking-tight mb-1 text-lg font-medium")}>
            Streamline Your Device Management
          </h2>
          <p className={cx("text-muted-foreground mb-3 max-w-sm text-sm", "text-muted-foreground mb-2 max-w-sm text-xs")}>
            Smplify helps you manage all your devices in one place,
            deploy configurations with ease, and maintain security
            compliance with minimal effort.
          </p>
          <div className={cx("flex flex-wrap gap-3 my-[16px]", "flex flex-wrap gap-2 my-2")}>
            <Button asChild size={"sm" as any}>
              <Link to="/devices">
                <Shield className="mr-2 h-4 w-4" /> Enroll Device
              </Link>
            </Button>
            <Button variant="outline" asChild size={"sm" as any}>
              <Link to="/configurations/new">
                <UserPlus className="mr-2 h-4 w-4" /> Create Profile
              </Link>
            </Button>
          </div>
        </div>
        <div className="md:w-2/5 flex justify-center">
          <img src="/lovable-uploads/40fc408c-1fe6-4629-be53-10996bedf8cd.png" alt="Device Management Illustration" className={cx("rounded-lg max-w-full h-auto w-2/3", "rounded-lg max-w-full h-auto w-1/2")} />
        </div>
      </div>

      <div className={cx("mb-8 my-0 py-[16px]", "mb-4 my-0 py-2")}>
        <h2 className={cx("text-2xl font-semibold tracking-tight mb-6 text-center", "text-xl font-semibold tracking-tight mb-4 text-center")}>Key Setup Components</h2>
        <div className="flex flex-col md:flex-row items-start gap-4 justify-between">
          {setupSteps.map((step, index) => <div key={step.id} className="flex-1 relative">
              {index < setupSteps.length - 1 && <div className={`hidden md:block absolute ${cx("top-6", "top-5")} h-0.5 bg-muted -translate-y-1/2`} style={{
            left: '50%',
            width: '100%',
            zIndex: 0
          }}>
                  {step.completed && <div className="absolute top-0 left-0 h-full bg-primary" style={{
              width: setupSteps[index + 1].completed ? '100%' : '50%'
            }}></div>}
                </div>}
              
              <div className="flex flex-col items-center">
                <div className={`${cx("w-12 h-12", "w-10 h-10")} rounded-full flex items-center justify-center mb-3 relative z-10 ${step.completed ? 'bg-primary/10 text-primary' : index === completedSteps ? 'bg-accent text-accent-foreground' : 'bg-muted text-muted-foreground'}`}>
                  {step.completed ? <ArrowRight className={cx("h-6 w-6", "h-5 w-5")} /> : <span className="font-bold">{index + 1}</span>}
                </div>
                <h3 className={`font-medium text-center ${cx("text-sm", "text-xs")} mb-1 relative z-10 h-6 flex items-center justify-center ${step.completed ? 'text-primary' : index === completedSteps ? 'text-accent-foreground' : 'text-muted-foreground'}`}>{step.name}</h3>
                {step.completed && <span className="text-xs text-primary">Completed</span>}
              </div>
            </div>)}
        </div>
      </div>

      <div className={cx("text-center mt-8", "text-center mt-4")}>
        <h3 className={cx("font-medium mb-2 text-base", "font-medium mb-1 text-sm")}>For Additional Assistance</h3>
        <p className={cx("text-muted-foreground mb-4 text-sm", "text-muted-foreground mb-2 text-xs")}>
          Our support team is available to help you with any questions or issues.
        </p>
        <Button variant="outline" size={"sm" as any} className={cx("my-[10px]", "my-1")}>Contact Support</Button>
      </div>
    </div>;
};

export default GettingStarted;
